import React, { useEffect, useState, useCallback } from "react";
import { useStyles } from "./AutoSync.styles";
import { Grid, Button, FormControlLabel, Switch } from "@material-ui/core";
import clsx from "clsx";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { useConfirmationDialog } from "../../../../hooks/useConfirmationDialog";
import apiClient from "../../../../auth/apiClient";
import RoutineService from "../../../../services/RoutineService";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import useCurrentFacility from "../../../../hooks/useCurrentFacility";
import { ROUTINE_TYPE } from "../../../../constants/index";
import ScheduleTable from "./ScheduleTable/ScheduleTable";

const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const DAY_TO_CRON = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
};

const AutoSyncPage = () => {
    const classes = useStyles();
    const routineService = new RoutineService(apiClient);
    const { showDialog } = useConfirmationDialog();
    const enqueueSnackbar = useEnqueueSnackbar();
    const { facilityID } = useCurrentFacility();

    const methods = useForm({
        defaultValues: {
            isAutoSyncEnable: false,
        },
        mode: "onChange",
    });
    const { control, handleSubmit, reset } = methods;

    const [routineId, setRoutineId] = useState(0);

    const defaultRoutine = {
        id: routineId ?? 0,
        entityId: facilityID,
        name: "Access Group Auto Sync",
        cronSchedule: null,
        systemEventId: null,
        actions: [],
    };

    const [currentRoutine, setCurrentRoutine] = useState(defaultRoutine);

    const [schedule, setSchedule] = useState(
        dayNames.map((day) => ({ day, enabled: false, time: null }))
    );

    const handleScheduleChange = useCallback((updatedSchedule) => {
        setSchedule(updatedSchedule);
    }, []);

    const handleSyncNow = async (checked) => {
        if (checked) {
            const response = await showDialog({
                title: "Sync Now Confirmation",
                message:
                    "Please confirm you want to sync access groups with auto sync enabled now?",
                buttons: [
                    { name: "Submit", color: "primary" },
                    { name: "Cancel", color: "secondary" },
                ],
            });
            if (response === "Cancel") return;
        }
    };

    const parseAmPm = (timeStr) => {
        if (typeof timeStr !== 'string') {
            throw new Error('time must be a string in AM/PM format');
        }
        const s = timeStr.trim().toUpperCase();
        // Regex: capture hour, minute, and AM/PM with optional space before AM/PM
        const m = s.match(/^(\d{1,2}):(\d{1,2})\s*(AM|PM)$/);
        if (!m) {
            throw new Error(`Invalid time format: "${timeStr}". Expected "h:mm AM/PM"`);
        }
        let hour = parseInt(m[1], 10);
        let minute = parseInt(m[2], 10);
        const meridiem = m[3]; // "AM" or "PM"
        // Validate ranges
        if (!(hour >= 1 && hour <= 12) || !(minute >= 0 && minute <= 59)) {
            throw new Error(`Invalid time values: hour=${hour}, minute=${minute}`);
        }
        // Convert to 24-hour
        if (meridiem === 'AM') {
            hour = (hour === 12) ? 0 : hour; // 12:xx AM -> 00:xx
        } else {
            hour = (hour === 12) ? 12 : hour + 12;// PM: 12:xx PM stays 12, otherwise add 12
        }
        const hour24 = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');

        return { hour: hour24, minute: minuteStr };
    }

    const createCronSchedules = (payload) => {
        if (!payload) return '[]';

        const cronArray = (payload?.schedule ?? [])
            .filter(p =>
                p.enabled &&
                p.time &&
                p.day.toLowerCase() !== "all"
            )
            .map(p => {
                const timeStr = (typeof p.time === 'string' && p.time.trim()) ? p.time.trim() : "00:00 AM";
                const { hour, minute } = parseAmPm(timeStr);
                const dayOfWeek = DAY_TO_CRON[p.day.toLowerCase()];

                return `${minute} ${hour} * * ${dayOfWeek}`;
            })

        return JSON.stringify(cronArray);
    }

    const buildAutoSyncAction = () => {
        const schedulePayload = schedule
            .filter(({ enabled, time }) => enabled)
            .map(({ day, enabled, time }) => ({
                day,
                enabled,
                time: time || null,
            }));

        const existing = currentRoutine?.actions?.find(
            (a) => a.actionTypeID === ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC
        );
        const order = existing?.order ?? (currentRoutine?.actions?.length || 0) + 1;
        const autoSyncEnable = methods.getValues("isAutoSyncEnable");

        return {

            payload: {
                isAutoSyncEnable: autoSyncEnable,
                schedule: schedulePayload,
            },
            actionTypeID: ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC,
            order,
        };
    };

    const upsertAction = (actions, action) => {
        const idx = actions.findIndex((a) => a.order === action.order);
        if (idx >= 0) {
            const copy = [...actions];
            copy[idx] = action;
            return copy;
        }
        return [...actions, action];
    };

    const persistRoutine = async (currentRoutine) => {
        try {
            if (currentRoutine?.id != null && currentRoutine?.id > 0) {
                var response = await routineService.updateRoutine(currentRoutine);
                setCurrentRoutine(response?.data);
            } else {
                var response = await routineService.createRoutine(currentRoutine);
                setCurrentRoutine(response?.data);
            }
        } catch (error) {
            enqueueSnackbar("Failed to save auto sync routine", {
                variant: "error",
                tag: "saveAutoSyncRoutineError"
            });
        }
    };

    const errorToast = useCallback((msg) => {
        enqueueSnackbar(msg, { variant: "error" });
    }, []);

    const onSaveClick = useCallback(async () => {
        const hasMissingTime = schedule.some(row => row.enabled && !row.time);
        if (hasMissingTime) {
            errorToast("Unable to save, please select time for all enabled days");
            return;
        }
        const action = buildAutoSyncAction();
        const nextActions = upsertAction(currentRoutine.actions ?? [], action);
        const cronSchedules = createCronSchedules(nextActions[0]?.payload || '[]');
        const nextRoutine = { ...currentRoutine, actions: nextActions, cronSchedule: cronSchedules };
        //await persistRoutine(nextRoutine);  // NOTE: This will be uncommented after backend story AO-29339 is complete (dependency)
    });

    const GetRoutine = async () => {
        try {
            const result = await routineService.getAutoSynceRoutine(facilityID);
            const routine = result?.data;
            setCurrentRoutine(routine ?? defaultRoutine);
            setRoutineId(routine?.id ?? 0);
            const autoSyncAction = routine?.actions?.find(
                (a) => a.actionTypeID === ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC
            );
            const payload = autoSyncAction?.payload;
            if (payload) {
                const autosyncEnable = Boolean(payload.isAutoSyncEnable);
                const newValues = {
                    isAutoSyncEnable: autosyncEnable,
                };
                reset(newValues);
                const scheduleArray = Array.isArray(payload.schedule)
                    ? payload.schedule
                    : [];
                const normalized = dayNames.map((day) => {
                    const found = scheduleArray.find((p) => String(p.day) === day);
                    return {
                        day,
                        enabled: Boolean(found?.enabled),
                        time:
                            typeof found?.time === "string" && found?.time.trim()
                                ? found.time.trim()
                                : null,
                    };
                });

                setSchedule(normalized);
            } else {
                const newValues = {
                    isAutoSyncEnable: false,
                };
                reset(newValues);
                setSchedule(
                    dayNames.map((day) => ({
                        day,
                        enabled: false,
                        time: null,
                    }))
                );
            }
        } catch (error) {
            enqueueSnackbar("Failed to get auto sync routine", {
                variant: "error",
                tag: "getSystemEventsError",
            });
        }
    };

    useEffect(() => {
        if (facilityID) GetRoutine();
    }, [facilityID]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSaveClick)}>
                <Grid item xs={12} className={classes.headerContainer}>
                    <Grid item xs={6}>
                        <h2 className={clsx(classes.text)}>Auto Sync Schedule</h2>
                    </Grid>
                    <Grid item xs={6} className={classes.syncNowBtnGrp}>
                        <Button
                            color="primary"
                            variant="contained"
                            data-testid="syncnow-btn"
                            onClick={handleSyncNow}
                        >
                            SYNC NOW
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="isAutoSyncEnable"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                role="isAutoSyncEnable-toggle"
                                label="Auto Sync Enable"
                                control={
                                    <Switch
                                        {...field}
                                        id="isAutoSyncEnable"
                                        color="primary"
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            field.onChange(checked);
                                        }}
                                        checked={!!field.value}
                                        data-checked={!!field.value}
                                    />
                                }
                                className={clsx([classes.ToggleContainer])}
                            />
                        )}
                    />
                </Grid>

                <ScheduleTable initialSchedule={schedule} onScheduleChange={handleScheduleChange} />

                <div className={classes.saveBtnGrp}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        data-testid="save-btn"
                    >
                        Save
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default AutoSyncPage;